import { useCallback, useEffect, useRef, useState } from 'react';
import type { ClientSummary } from '@/features/Clients/Data';
import { calcularAlertaMovimentacao, CLASSE_COR_POR_NIVEL_ALERTA } from '../../../models';
import { TIPO_SOLICITACAO_LABELS } from '../../NovaSolicitacaoPage/novaSolicitacaoPage.definitions';
import { STATUS_SOLICITACAO_LABELS, STATUS_SOLICITACAO_BADGE_CLASS } from '../solicitacoesListPage.definitions';
import { pendenciaMovimentacao, pendenciaSolicitacao } from '../../SolicitacaoDetailsPage/pendenciaMovimentacao';
import SolicitacaoDetailsContent from '../../SolicitacaoDetailsPage/SolicitacaoDetailsContent';
import { movimentacaoRelevante, type LinhaControle, type VisaoControle } from '../solicitacoesLista.utils';
import { dataPrevistaComFluxo } from './solicitacoesKanban.utils';

interface SolicitacoesSplitViewProps {
  linhas: LinhaControle[];
  visao: VisaoControle;
  clientes: ClientSummary[];
  /** Só na aba "Controle de Solicitações" — mesmo critério do resto da tela. */
  mostrarPendencia: boolean;
  /** Chave da linha selecionada (LinhaControle.chave). */
  selecionadaChave: string | null;
  forcarVisaoSolicitante: boolean;
  onSelect: (chave: string) => void;
}

/** Na visão Movimentações, o detalhe abre na Movimentação clicada. */
function porMovimentacaoNaVisao(visao: VisaoControle, linha: LinhaControle): string | null {
  return visao === 'movimentacoes' ? linha.movimentacao?.id ?? null : null;
}

const LARGURA_LISTA_PADRAO = 42;
const LARGURA_LISTA_MIN = 20;
const LARGURA_LISTA_MAX = 65;
const CHAVE_LARGURA_PERSISTIDA = 'si:solicitacoesSplitViewLargura';

/** Não trava se não achar nada válido salvo — cai no padrão de sempre. */
function larguraInicial(): number {
  try {
    const valor = Number(localStorage.getItem(CHAVE_LARGURA_PERSISTIDA));
    if (!Number.isNaN(valor) && valor >= LARGURA_LISTA_MIN && valor <= LARGURA_LISTA_MAX) return valor;
  } catch {
    // localStorage indisponível — só usa o padrão, não é crítico.
  }
  return LARGURA_LISTA_PADRAO;
}

/**
 * EXPERIMENTO (branch `experimento/layout-solicitacoes`): lista à esquerda + detalhe
 * embutido à direita, na mesma tela — sem abrir modal/drawer nenhum. A divisória entre os
 * dois painéis é arrastável (largura inicial maior que a 1ª versão, que ficou estreita
 * demais pro gosto do usuário) — arrasta pra ajustar, sem limite rígido de "certo". A
 * largura escolhida fica salva (localStorage) — só grava ao soltar o arraste, não a cada
 * pixel movido, pra não escrever à toa.
 */
export default function SolicitacoesSplitView({
  linhas,
  visao,
  clientes,
  mostrarPendencia,
  selecionadaChave,
  forcarVisaoSolicitante,
  onSelect,
}: SolicitacoesSplitViewProps) {
  const selecionada = linhas.find((linha) => linha.chave === selecionadaChave) ?? null;
  const containerRef = useRef<HTMLDivElement>(null);
  const [larguraLista, setLarguraLista] = useState(larguraInicial);
  const larguraRef = useRef(larguraLista);
  const arrastando = useRef(false);

  useEffect(() => {
    larguraRef.current = larguraLista;
  }, [larguraLista]);

  const handleMouseMove = useCallback((event: MouseEvent) => {
    if (!arrastando.current || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const percentual = ((event.clientX - rect.left) / rect.width) * 100;
    setLarguraLista(Math.min(LARGURA_LISTA_MAX, Math.max(LARGURA_LISTA_MIN, percentual)));
  }, []);

  const pararArraste = useCallback(() => {
    if (!arrastando.current) return;
    arrastando.current = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    try {
      localStorage.setItem(CHAVE_LARGURA_PERSISTIDA, String(larguraRef.current));
    } catch {
      // localStorage indisponível — largura simplesmente não persiste, não é crítico.
    }
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', pararArraste);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', pararArraste);
    };
  }, [handleMouseMove, pararArraste]);

  const iniciarArraste = () => {
    arrastando.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  return (
    <div ref={containerRef} className="flex" style={{ height: '80vh' }}>
      <div
        className="flex-shrink-0 overflow-y-auto rounded-l-md border border-gray-200"
        style={{ width: `${larguraLista}%` }}
      >
        {linhas.length === 0 && <p className="p-4 text-sm text-gray-400">Nenhuma solicitação encontrada.</p>}
        {linhas.map((linha) => {
          const { solicitacao, movimentacoes } = linha;
          const porMovimentacao = visao === 'movimentacoes' && linha.movimentacao != null;
          const movimentacao = porMovimentacao ? linha.movimentacao : movimentacaoRelevante(movimentacoes);
          const alerta = movimentacao ? calcularAlertaMovimentacao(movimentacao) : null;
          const pendencia = !mostrarPendencia || solicitacao.status !== 'em_andamento'
            ? null
            : porMovimentacao && linha.movimentacao
              ? pendenciaMovimentacao(linha.movimentacao)
              : pendenciaSolicitacao(solicitacao, movimentacoes);
          const clienteId = (porMovimentacao ? linha.movimentacao?.clienteId : null) ?? solicitacao.clienteId;
          const clienteNome = clientes.find((cliente) => cliente.id === clienteId)?.name ?? null;
          const data = dataPrevistaComFluxo(linha.movimentacao);
          const ativa = linha.chave === selecionadaChave;

          return (
            <button
              key={linha.chave}
              type="button"
              onClick={() => onSelect(linha.chave)}
              className={`block w-full border-b border-gray-100 px-3 py-2 text-left text-sm last:border-b-0 ${
                ativa ? 'bg-blue-50' : 'bg-white hover:bg-gray-50'
              }`}
            >
              <p className="truncate font-montserrat font-medium text-gray-900">{solicitacao.colaboradorNome}</p>
              <p className="truncate text-xs text-gray-500">
                {TIPO_SOLICITACAO_LABELS[solicitacao.tipoSolicitacao]}
                {clienteNome && ` · ${clienteNome}`}
              </p>
              {data && <p className="truncate text-xs text-gray-400">{data}</p>}
              <span className="mt-1 flex flex-wrap gap-1">
                {alerta && (
                  <span className={`rounded-full px-1.5 py-0.5 text-xs font-semibold ${CLASSE_COR_POR_NIVEL_ALERTA[alerta.nivel]}`}>
                    {alerta.emoji} {alerta.texto}
                  </span>
                )}
                {pendencia && (
                  <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-800">
                    {pendencia.emoji} {pendencia.label}
                  </span>
                )}
                <span className={`rounded-full px-1.5 py-0.5 text-xs font-semibold ${STATUS_SOLICITACAO_BADGE_CLASS[solicitacao.status]}`}>
                  {STATUS_SOLICITACAO_LABELS[solicitacao.status]}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      {/* Divisória arrastável — mais larga que uma borda normal (px-1.5) pra facilitar o
          clique, com um traço central só pra indicar visualmente que é arrastável. */}
      <div
        onMouseDown={iniciarArraste}
        className="group flex w-2 flex-shrink-0 cursor-col-resize items-center justify-center border-y border-gray-200 bg-gray-100 hover:bg-blue-100"
        title="Arraste pra ajustar a largura"
      >
        <div className="h-8 w-0.5 rounded-full bg-gray-300 group-hover:bg-blue-400" />
      </div>

      <div className="flex-1 overflow-y-auto rounded-r-md border border-gray-200">
        {selecionada ? (
          <SolicitacaoDetailsContent
            key={selecionada.chave}
            id={selecionada.solicitacao.id}
            movimentacaoInicialId={porMovimentacaoNaVisao(visao, selecionada)}
            forcarVisaoSolicitante={forcarVisaoSolicitante}
          />
        ) : (
          <p className="p-6 text-sm text-gray-400">Selecione uma Solicitação na lista à esquerda.</p>
        )}
      </div>
    </div>
  );
}
